import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  CareerRoleFullPictureResponse,
  RoleResourceOrphanMappingResponse,
  SkillTopicItemDto,
  SkillWithResourcesResponse
} from '../../../../models';
import { CareerRoleAdminActionsService } from '../shared/career-role-admin-actions.service';
import { CareerRoleFullPictureStore } from '../shared/career-role-full-picture.store';
import { findCareerRoleSlugRoute } from '../shared/career-role-route.util';

interface TopicTargetOption {
  skillId: string;
  skillName: string;
  topicName: string;
  label: string;
}

@Component({
  selector: 'app-resources-revalidate-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule
  ],
  templateUrl: './resources-revalidate-tab.component.html',
  styleUrl: './resources-revalidate-tab.component.scss'
})
export class ResourcesRevalidateTabComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly pictureStore = inject(CareerRoleFullPictureStore);
  private readonly snackBar = inject(MatSnackBar);
  private readonly fb = inject(FormBuilder);
  private slugSubscription?: Subscription;

  private readonly slugSignal = signal('');
  readonly roleState = computed(() => {
    const slug = this.slugSignal();
    if (!slug) {
      return null;
    }
    return this.pictureStore.getState(slug) ?? null;
  });

  readonly actionBusy = signal(false);
  error = '';
  orphans: RoleResourceOrphanMappingResponse[] = [];
  topicTargets: TopicTargetOption[] = [];

  readonly remapForms = new Map<string, FormGroup>();
  readonly publishThreshold = this.fb.nonNullable.control(0.45, [
    Validators.required,
    Validators.min(0),
    Validators.max(1)
  ]);

  get slug(): string {
    return this.slugSignal();
  }

  get picture(): CareerRoleFullPictureResponse | null {
    return this.roleState()?.picture ?? null;
  }

  get loading(): boolean {
    return (this.roleState()?.loading ?? false) || this.actionBusy();
  }

  get refreshing(): boolean {
    return this.roleState()?.refreshing ?? false;
  }

  ngOnInit(): void {
    const slugRoute = findCareerRoleSlugRoute(this.route);
    if (!slugRoute) {
      this.error = 'Role slug is missing from the route.';
      return;
    }
    this.slugSubscription = slugRoute.paramMap.subscribe((params) => {
      this.slugSignal.set(params.get('slug') ?? '');
      void this.load();
    });
  }

  ngOnDestroy(): void {
    this.slugSubscription?.unsubscribe();
  }

  async load(): Promise<void> {
    if (!this.slug) {
      return;
    }
    this.actionBusy.set(true);
    this.error = '';
    try {
      const picture = await this.pictureStore.load(this.slug, { background: !!this.picture });
      if (!picture) {
        return;
      }
      this.topicTargets = this.collectTopicTargets(picture);
      this.orphans = await firstValueFrom(this.api.listRoleResourceOrphanMappings(this.slug));
      this.remapForms.clear();
      for (const orphan of this.orphans) {
        this.remapForms.set(
          orphan.mappingId,
          this.fb.nonNullable.group({
            targetLabel: [this.topicTargets[0]?.label ?? '', Validators.required]
          })
        );
      }
    } catch (error) {
      this.error = this.actions.errorMessage(error);
      this.snackBar.open(this.error, 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async remap(orphan: RoleResourceOrphanMappingResponse): Promise<void> {
    const form = this.remapForms.get(orphan.mappingId);
    if (!form || form.invalid) {
      return;
    }
    const label = form.value.targetLabel as string;
    const target = this.topicTargets.find((t) => t.label === label);
    if (!target) {
      return;
    }
    this.actionBusy.set(true);
    try {
      await firstValueFrom(
        this.api.remapRoleResourceMapping(this.slug, {
          mappingId: orphan.mappingId,
          targetSkillId: target.skillId,
          targetTopicName: target.topicName
        })
      );
      this.snackBar.open('Mapping remapped', 'Close', { duration: 3000 });
      await Promise.all([
        this.pictureStore.refreshResourceCounts(this.slug),
        this.loadOrphansOnly()
      ]);
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  private async loadOrphansOnly(): Promise<void> {
    this.orphans = await firstValueFrom(this.api.listRoleResourceOrphanMappings(this.slug));
    this.remapForms.clear();
    for (const orphan of this.orphans) {
      this.remapForms.set(
        orphan.mappingId,
        this.fb.nonNullable.group({
          targetLabel: [this.topicTargets[0]?.label ?? '', Validators.required]
        })
      );
    }
  }

  async recomputeScores(): Promise<void> {
    const skillIds = [...new Set(this.orphans.map((o) => o.skillId))];
    if (!skillIds.length && this.picture) {
      for (const profile of this.picture.seniorityProfiles) {
        for (const skill of profile.skills ?? []) {
          skillIds.push(skill.skill.id);
        }
      }
    }
    this.actionBusy.set(true);
    try {
      let updated = 0;
      for (const skillId of [...new Set(skillIds)]) {
        const result = await firstValueFrom(
          this.api.recomputeSkillResourceMappingScores({ skillId })
        );
        updated += result.mappingsUpdated ?? 0;
      }
      this.snackBar.open(`Recomputed scores for ${updated} mappings`, 'Close', { duration: 4000 });
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async validateResources(): Promise<void> {
    if (this.publishThreshold.invalid) {
      return;
    }
    this.actionBusy.set(true);
    try {
      const skillIds = [
        ...new Set([
          ...this.orphans.map((o) => o.skillId),
          ...(this.picture?.seniorityProfiles.flatMap((p) =>
            (p.skills ?? []).map((s) => s.skill.id)
          ) ?? [])
        ])
      ];
      for (const skillId of skillIds.slice(0, 25)) {
        const run = await firstValueFrom(
          this.api.validateCommittedResourcesForSkill(skillId, {
            publishThreshold: this.publishThreshold.value
          })
        );
        this.actions.trackExternalRun(run, `Validate resources: ${skillId}`, this.slug);
      }
      this.snackBar.open('Validation jobs queued', 'Close', { duration: 4000 });
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async gapFillAll(): Promise<void> {
    if (!this.picture) {
      return;
    }
    this.actionBusy.set(true);
    try {
      const skills = this.collectSkills(this.picture);
      for (const skill of skills) {
        const topics = this.resolveTopics(skill);
        for (const topic of topics) {
          await this.actions.populateSkillResourcesGapFill(
            this.slug,
            skill.skill.id,
            topic.name,
            this.publishThreshold.value,
            () => this.pictureStore.onBackgroundJobComplete(this.slug, 'populate_skill_resources')
          );
        }
      }
      this.snackBar.open('Gap-fill jobs queued', 'Close', { duration: 4000 });
    } catch (error) {
      this.snackBar.open(this.actions.errorMessage(error), 'Close', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  private collectSkills(picture: CareerRoleFullPictureResponse): SkillWithResourcesResponse[] {
    const skills: SkillWithResourcesResponse[] = [];
    for (const profile of picture.seniorityProfiles) {
      skills.push(...(profile.skills ?? []));
    }
    for (const spec of picture.specializations) {
      for (const profile of spec.seniorityProfiles) {
        skills.push(...(profile.skills ?? []));
      }
    }
    return skills;
  }

  private collectTopicTargets(picture: CareerRoleFullPictureResponse): TopicTargetOption[] {
    const options: TopicTargetOption[] = [];
    for (const skill of this.collectSkills(picture)) {
      for (const topic of this.resolveTopics(skill)) {
        options.push({
          skillId: skill.skill.id,
          skillName: skill.skill.name,
          topicName: topic.name,
          label: `${skill.skill.name} / ${topic.name}`
        });
      }
    }
    return options;
  }

  private resolveTopics(skill: SkillWithResourcesResponse): SkillTopicItemDto[] {
    const facets = skill.topics;
    if (!facets?.length) {
      return [];
    }
    const byName = new Map<string, SkillTopicItemDto>();
    for (const f of facets) {
      if (typeof f === 'string') {
        const name = f.trim();
        if (!name) continue;
        byName.set(name.toLowerCase(), { name, level: skill.requiredProficiency, importance: skill.importance });
        continue;
      }
      const name = f.name?.trim();
      if (!name) continue;
      byName.set(name.toLowerCase(), f);
    }
    return [...byName.values()];
  }
}